import React, { useEffect } from 'react';
import { get, isEmpty } from 'lodash-es';
import { connect } from 'react-redux';

/* Models */
import {
  buildDefaultVehiclePath,
  buildStylesPath,
  buildVehicleStylePath,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import { ComparatorModel, getBestComparatorVehiclesPath } from 'client/data/models/comparator';
import { buildMMYSubmodelTrimPhotosPathWithParams, PhotosModel } from 'client/data/models/photos';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { getPageLinksPath, LinkWidgetsModel } from 'client/data/models/link-widgets';

/* Utils */
import { pageDefinition } from 'site-modules/shared/pages/page';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { getModelState } from 'client/data/luckdragon/model';
import { create404Error } from 'site-modules/shared/utils/errors';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { getBaseBreadcrumb, getHomeBreadcrumb } from 'site-modules/shared/components/breadcrumbs/helper';
import { parseVehicleForAction } from 'client/utils/parse-vehicle-for-action';
import { updateVehicle } from 'client/actions/vehicle';
import { EventToolbox } from 'client/utils/event-toolbox';
import { getColumnIdsString } from 'site-modules/comparator/utils/comparator';
import { setAdsListToPreload } from 'site-modules/shared/components/native-ad/utils/utils';
import { setCriticalCss } from 'client/actions/critical-css';

/* Constants */
import { COMPARATOR_PATHNAME, MAX_COMPARE_SIZE } from 'site-modules/comparator/constants/comparator';
import { INCENTIVES_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { TrackingConstant } from 'client/tracking/constant';

/* Components */
import { ComparatorUI, mapStateToProps } from 'site-modules/comparator/pages/comparator/comparator';
import { EdmundsDefaultDecorator } from 'site-modules/shared/pages/decorators/edmunds-default';
import { buildSegmentRatingsPath, TypeRankingsFeaturesModel } from 'client/data/models/type-rankings-features';
import { decoratorParams } from 'site-modules/shared/pages/decorators/decorator-params';
import { MM_VS_MM_SUMMARY } from 'site-modules/comparator/constants/page-summary';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { HomeModulesModel } from 'client/data/models/home-modules';
import { transform } from 'client/data/cms/content';

export function MakeModelVsMakeModelUI(props) {
  useEffect(() => {
    const { make1, model1, make2, model2, make3, model3, make4, model4 } = props.match.params;

    EventToolbox.fireTrackAction({
      event_type: 'action_completed',
      event_data: {
        action_name: TrackingConstant.ACTION_LIST,
        action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        make1,
        model1,
        make2,
        model2,
        make3,
        model3,
        make4,
        model4,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <ComparatorUI {...props} isMMvsMMComparator />;
}

export const buildStylePath = styles => {
  const styleId = get(styles, '[0].id');
  return styleId && buildVehicleStylePath({ styleId });
};

export const stateToPropsConfig = {
  suggestedLinks: bindToPath(() => 'suggestedLinks', ComparatorModel),
  sponsorship: bindToPath(({ styles1 }) => {
    const styleId = get(styles1, '[0].id');
    return styleId ? `sponsorship.byStyle["${styleId}"]` : 'sponsorship.empty';
  }, ComparatorModel),
  veh1: bindToPath(({ styles1 }) => buildStylePath(styles1), VehicleModel),
  veh2: bindToPath(({ styles2 }) => buildStylePath(styles2), VehicleModel),
  veh3: bindToPath(({ styles3 }) => buildStylePath(styles3), VehicleModel),
  veh4: bindToPath(({ styles4 }) => buildStylePath(styles4), VehicleModel),
  hasEvVehicle: bindToPath(
    ({ styles1, styles2, styles3, styles4 }) =>
      `evEfficiency["${getColumnIdsString(
        [get(styles1, '[0]'), get(styles2, '[0]'), get(styles3, '[0]'), get(styles4, '[0]')].map(veh => ({
          styleId: get(veh, 'id'),
        }))
      )}"]`,
    ComparatorModel,
    evComparableFeatures =>
      evComparableFeatures.some(
        evComparableFeature => get(evComparableFeature, 'isElectric') || get(evComparableFeature, 'isPluginElectric')
      )
  ),
  vehicleShowcaseContent: bindToPath('vehicleShowcaseContent', HomeModulesModel, transform),
};

const MakeModelVsMakeModel = connect(mapStateToProps)(connectToModel(MakeModelVsMakeModelUI, stateToPropsConfig));

export const buildVehicleStylesPath = mmsy =>
  mmsy &&
  buildStylesPath({
    make: mmsy.make.slug,
    model: mmsy.model.slug,
    submodel: mmsy.submodels.slug,
    year: mmsy.year,
  });

export const secondWrapperStateToPropsConfig = {
  styles1: bindToPath(({ mmsy1 }) => buildVehicleStylesPath(mmsy1), VehicleModel),
  styles2: bindToPath(({ mmsy2 }) => buildVehicleStylesPath(mmsy2), VehicleModel),
  styles3: bindToPath(({ mmsy3 }) => buildVehicleStylesPath(mmsy3), VehicleModel),
  styles4: bindToPath(({ mmsy4 }) => buildVehicleStylesPath(mmsy4), VehicleModel),
};

const MakeModelVsMakeModelSecondWrapper = connectToModel(MakeModelVsMakeModel, secondWrapperStateToPropsConfig);

export const wrapperStateToPropsConfig = {
  mmsy1: bindToPath(
    ({ match }) => buildDefaultVehiclePath({ make: match.params.make1, model: match.params.model1 }),
    VehicleModel
  ),
  mmsy2: bindToPath(
    ({ match }) => buildDefaultVehiclePath({ make: match.params.make2, model: match.params.model2 }),
    VehicleModel
  ),
  mmsy3: bindToPath(
    ({ match }) => buildDefaultVehiclePath({ make: match.params.make3, model: match.params.model3 }),
    VehicleModel
  ),
  mmsy4: bindToPath(
    ({ match }) => buildDefaultVehiclePath({ make: match.params.make4, model: match.params.model4 }),
    VehicleModel
  ),
};

const throw404Error = () => {
  throw create404Error('Could not find one or more matching vehicles');
};

const MakeModelVsMakeModelWrapper = connectToModel(MakeModelVsMakeModelSecondWrapper, wrapperStateToPropsConfig);

export const pageDefinitionConfiguration = {
  name: 'comparator_make_model_vs_make_model',
  category: 'comparator',
  seo: (state, props) => {
    const { make1, model1, make2, model2, make3, model3, make4, model4 } = props.match.params;
    const modelState = getModelState(state);

    const [vehicle1, vehicle2, vehicle3, vehicle4] = [
      { make: make1, model: model1 },
      { make: make2, model: model2 },
      { make: make3, model: model3 },
      { make: make4, model: model4 },
    ]
      .map(vehicle => buildDefaultVehiclePath(vehicle))
      .filter(Boolean)
      .map(vehiclePath => modelState.get(vehiclePath, VehicleModel));

    if (!vehicle1 || !vehicle2) {
      throw404Error();
    }

    const makeModelVsMakeModelTitle = [vehicle1, vehicle2, vehicle3, vehicle4]
      .filter(Boolean)
      .map(({ make: { name: makeName }, model: { name: modelName }, year }) => `${year} ${makeName} ${modelName}`)
      .join(' vs ');

    return {
      title: `${makeModelVsMakeModelTitle} - Car Comparison`,
      description: `${makeModelVsMakeModelTitle} - which is better for you? Find out with Edmunds' head-to-head car comparison tool. Compare prices, options, features and specs, expert and consumer reviews and more.`,
      jsonld: [
        getBreadcrumbSchema([
          getHomeBreadcrumb(),
          getBaseBreadcrumb('Compare cars', COMPARATOR_PATHNAME),
          getBaseBreadcrumb(makeModelVsMakeModelTitle, props.location.pathname),
        ]),
      ],
    };
  },
  preload: (store, props) => {
    const { make1, model1, make2, model2, make3, model3, make4, model4 } = props.match.params;
    const preloader = new ModelPreloader(store);

    const vehiclesPaths = [
      { make: make1, model: model1 },
      { make: make2, model: model2 },
      { make: make3, model: model3 },
      { make: make4, model: model4 },
    ]
      .map(vehicle => buildDefaultVehiclePath(vehicle))
      .filter(Boolean);

    vehiclesPaths.forEach(vehiclePath => {
      preloader.resolve(vehiclePath, VehicleModel);
    });

    preloader.resolve('suggestedLinks', ComparatorModel);
    preloader.resolve(getBestComparatorVehiclesPath(), ComparatorModel);
    preloader.resolve(getPageLinksPath(), LinkWidgetsModel);

    const adsList = [INCENTIVES_AD.AD_NAME];
    preloader.set('adsListSkipExtraParams', NativeAdsCreativeConfigModel, adsList);
    preloader.set('pageAdsList', NativeAdsCreativeConfigModel, adsList);

    return preloader.load().then(modelState => {
      const vehicles = vehiclesPaths.map(vehiclePath => modelState.get(vehiclePath, VehicleModel)).filter(Boolean);
      vehicles.forEach(vehicle => {
        const {
          year,
          submodels: { slug: submodel },
          make: { slug: make },
          model: { slug: model },
        } = vehicle;

        preloader.resolve(
          buildStylesPath({
            make,
            model,
            year,
            submodel,
          }),
          VehicleModel
        );
      });

      const styleIds = [];

      return preloader.load().then(() => {
        vehicles.forEach(vehicle => {
          const {
            year,
            submodels: { slug: submodel },
            make: { slug: make },
            model: { slug: model },
          } = vehicle;

          const styles = modelState.get(
            buildStylesPath({
              make,
              model,
              year,
              submodel,
            }),
            VehicleModel
          );
          const firstStyleId = get(styles, '[0].id');
          if (firstStyleId) {
            preloader.resolve(buildVehicleStylePath({ styleId: firstStyleId }), VehicleModel);
            styleIds.push(firstStyleId);
          }

          setAdsListToPreload(preloader, store.getState(), adsList, parseVehicleForAction(vehicle), {
            skipSetList: true,
          });

          preloader.resolve(
            buildSegmentRatingsPath({ makeSlug: make, modelSlug: model, year }),
            TypeRankingsFeaturesModel
          );
        });

        // Generates the same data path as site-modules/comparator/components/features component so data is preloaded correctly
        const comparatorColumns = new Array(MAX_COMPARE_SIZE).fill().map((val, ind) => ({ styleId: styleIds[ind] }));
        const styleIdsString = getColumnIdsString(comparatorColumns);

        preloader.resolve(`comparableFeatures["${styleIdsString}"]`, ComparatorModel);
        preloader.resolve(`evEfficiency["${styleIdsString}"]`, ComparatorModel);
        preloader.resolve(`pricing["${styleIdsString}"]`, ComparatorModel);
        preloader.resolve(`editorialReview["${styleIdsString}"]`, ComparatorModel);
        preloader.resolve(`consumerRating["${styleIdsString}"]`, ComparatorModel);
        preloader.resolve(`tco["${styleIdsString}"]`, ComparatorModel);
        preloader.resolve(`colors["${styleIdsString}"]`, ComparatorModel);
        preloader.resolve('vehicleShowcaseContent', HomeModulesModel);
        preloader.resolve('homeHeroContent', HomeModulesModel);

        const isCore6982Enabled = ExperimentUtil.getForcedOrAssignedRecipeName({
          state: store.getState(),
          campaignName: 'core-6982-comparator-leads',
          defaultVal: 'ctrl',
        }).includes('chal');

        if (isCore6982Enabled) {
          preloader.resolve(`dealerCount["${styleIdsString}"]`, ComparatorModel);
        }

        return preloader.load().then(() => {
          styleIds.forEach((styleId, ind) => {
            const style = modelState.get(buildVehicleStylePath({ styleId }), VehicleModel);
            const { makeSlug, modelSlug, year, submodels, trimName } = style;

            preloader.resolve(
              buildMMYSubmodelTrimPhotosPathWithParams({
                make: makeSlug,
                model: modelSlug,
                submodel: submodels.name,
                trim: trimName,
                year,
              }),
              PhotosModel
            );

            if (ind === 0 && !isEmpty(style)) {
              store.dispatch(updateVehicle(parseVehicleForAction(style)));
            }
          });

          const ads11245Recipe = ExperimentUtil.getForcedOrAssignedRecipeName({
            state: store.getState(),
            campaignName: 'ads-11245',
            defaultVal: 'ctrl',
          });

          store.dispatch(
            setCriticalCss({
              criticalCssHeight: 2050,
              ads11245Recipe,
              hasPageSummary: !!MM_VS_MM_SUMMARY[props.location.pathname],
            })
          );

          return preloader.load();
        });
      });
    });
  },
  component: MakeModelVsMakeModelWrapper,
  decorator: decoratorParams(EdmundsDefaultDecorator, { skipFloodlight: true }),
  options: {
    hasVehicleContext: true,
  },
};

export default pageDefinition(pageDefinitionConfiguration);
